import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Avatar,
    CircularProgress,
    useTheme,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    alpha
} from '@mui/material';
import {
    Search as SearchIcon,
    PersonAdd as PersonAddIcon,
    MailOutline as MailIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersByOrganization } from '../../../redux/actions/userAction';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { tracker } from '../../../analytics';
import CustomButton from '../../../components/CustomButton';
import { backendClient } from '../../../api/backend';

const isValidEmail = (str) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);

const ShareContractModal = ({
    open,
    onClose,
    onShare,
    organizationName,
    contractId,
}) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const dispatch = useDispatch();
    const actual_user = useSelector((state) => state.auth?.user?.user);
    const { users, loading: reduxLoading } = useSelector((state) => state.user);

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userRoles, setUserRoles] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sharing, setSharing] = useState(false);

    // External invites state
    const [externalInvites, setExternalInvites] = useState([]); // [{email, role}]
    const [externalRoles, setExternalRoles] = useState({}); // {email: role}

    // Available roles for sharing
    const availableRoles = ['viewer', 'editor', 'reviewer'];
    const externalAvailableRoles = ['viewer', 'reviewer']; // No editor for externals

    useEffect(() => {
        if (open && actual_user?.organization_id) {
            dispatch(fetchUsersByOrganization(actual_user.organization_id));
        }
    }, [open, actual_user, dispatch]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setExternalInvites([]);
            setExternalRoles({});
        }
    }, [open]);

    // Filter users based on search term
    const filteredUsers = users.filter(
        (user) =>
            user.id !== actual_user?.id && // Exclude the actual user
            (user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Show external invite option whenever user is typing, clickable only when valid email
    const searchHasText = searchTerm.trim().length > 0 &&
        !users.some(u => u.email?.toLowerCase() === searchTerm.toLowerCase().trim()) &&
        !externalInvites.some(e => e.email === searchTerm.toLowerCase().trim());
    const searchIsValidEmail = searchHasText && isValidEmail(searchTerm.trim());

    const handleToggle = (user) => {
        setSelectedUsers(prevSelected => {
            const isSelected = prevSelected.some(u => u.id === user.id);

            if (isSelected) {
                const newUserRoles = {...userRoles};
                delete newUserRoles[user.id];
                setUserRoles(newUserRoles);
                return prevSelected.filter(u => u.id !== user.id);
            } else {
                if (!userRoles[user.id]) {
                    setUserRoles({
                        ...userRoles,
                        [user.id]: 'editor'
                    });
                }
                return [...prevSelected, user];
            }
        });
    };

    const handleSelectExternalEmail = (email) => {
        const normalizedEmail = email.toLowerCase().trim();
        if (externalInvites.some(e => e.email === normalizedEmail)) return;

        setExternalInvites(prev => [...prev, { email: normalizedEmail }]);
        setExternalRoles(prev => ({ ...prev, [normalizedEmail]: 'reviewer' }));
        setSearchTerm('');
    };

    const handleRemoveExternal = (email) => {
        setExternalInvites(prev => prev.filter(e => e.email !== email));
        setExternalRoles(prev => {
            const next = { ...prev };
            delete next[email];
            return next;
        });
    };

    const handleExternalRoleChange = (email, role) => {
        setExternalRoles(prev => ({ ...prev, [email]: role }));
    };

    const handleRoleChange = (userId, role) => {
        setUserRoles({
            ...userRoles,
            [userId]: role
        });
    };

    const handleSearchChange = (e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);

        ReactGA.event({ category: "Share Modal", action: "Search User", label: searchValue });
        tracker.event({ category: "Share Modal", action: "Search User", label: searchValue });
    };

    const handleShare = async () => {
        if (selectedUsers.length === 0 && externalInvites.length === 0) return;

        setSharing(true);
        try {
            // Handle internal users
            if (selectedUsers.length > 0) {
                const usersWithRoles = selectedUsers.map(user => ({
                    id: user.id,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    email: user.email,
                    role: userRoles[user.id] || 'viewer'
                }));
                await onShare(usersWithRoles);
            }

            // Handle external invites
            for (const invite of externalInvites) {
                const role = externalRoles[invite.email] || 'reviewer';
                await backendClient.inviteExternalCollaborator(contractId, invite.email, role);
            }

            ReactGA.event({
                category: "Share Modal",
                action: "Conversation Shared",
                label: `Shared with ${selectedUsers.length} internal, ${externalInvites.length} external`,
                value: selectedUsers.length + externalInvites.length,
            });
            tracker.event({
                category: "Share Modal",
                action: "Conversation Shared",
                label: `Shared with ${selectedUsers.length} internal, ${externalInvites.length} external`,
            });

            setSelectedUsers([]);
            setUserRoles({});
            setExternalInvites([]);
            setExternalRoles({});
            onClose();
        } catch (error) {
            console.error('Error sharing conversation:', error);
        } finally {
            setSharing(false);
        }
    };

    const totalSelected = selectedUsers.length + externalInvites.length;

    return (
        <Dialog
            open={open}
            onClose={!sharing ? onClose : undefined}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("share_contract")}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    {t("shareContractModal.description", "Select users from your organization to share this conversation with.")}
                </Typography>

                {/* Search bar */}
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t("shareContractModal.searchOrEmail", "Search by name or type an email to invite...")}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.common.black, 0.02),
                            py: 0.5
                        }
                    }}
                    sx={{ mb: 2 }}
                />

                {/* User list */}
                {reduxLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List sx={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        borderRadius: 1.5,
                        mb: 2
                    }}>
                        {/* Internal users list */}
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map((user) => {
                                const isSelected = selectedUsers.some((u) => u.id === user.id);

                                return (
                                    <ListItem
                                        key={user.id}
                                        sx={{
                                            flexDirection: currentLang === "ar" ? "row-reverse" : "row",
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                            '&:last-child': { borderBottom: 'none' },
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            },
                                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                                            padding: '8px 16px',
                                            display: 'flex',
                                            flexWrap: 'wrap'
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            cursor: 'pointer',
                                            mb: isSelected ? 1 : 0
                                        }} onClick={() => handleToggle(user)}>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                                    {user.first_name ? user.first_name[0] : ""}
                                                    {user.last_name ? user.last_name[0] : ""}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={`${user.first_name} ${user.last_name}`}
                                                secondary={user.email}
                                                sx={{ textAlign: currentLang === "ar" ? "right" : "left" }}
                                            />
                                            <Checkbox
                                                edge={currentLang === "ar" ? "start" : "end"}
                                                sx={{
                                                    marginLeft: currentLang === "ar" ? "auto" : 0,
                                                    marginRight: currentLang !== "ar" ? "auto" : 0,
                                                    color: theme.palette.primary.main
                                                }}
                                                checked={isSelected}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggle(user);
                                                }}
                                            />
                                        </Box>

                                        {/* Role selection - only show if user is selected */}
                                        {isSelected && (
                                            <Box sx={{ width: '100%', pl: 7, pr: 2, pb: 1 }}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>{t("shareContractModal.role", "Role")}</InputLabel>
                                                    <Select
                                                        value={userRoles[user.id] || 'viewer'}
                                                        label={t("shareContractModal.role", "Role")}
                                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {availableRoles.map((role) => (
                                                            <MenuItem key={role} value={role}>
                                                                {t(`shareContractModal.role${role.charAt(0).toUpperCase() + role.slice(1)}`, role)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        )}
                                    </ListItem>
                                );
                            })
                        ) : (
                            !searchHasText && (
                                <Box sx={{
                                    py: 4,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    color: theme.palette.text.secondary
                                }}>
                                    <Typography variant="body2">
                                        {t("shareContractModal.noUsersFound", "No users found")}
                                    </Typography>
                                </Box>
                            )
                        )}

                        {/* External invite option — always shown at bottom when user is typing, clickable only when valid email */}
                        {searchHasText && (
                            <ListItem
                                onClick={searchIsValidEmail ? () => handleSelectExternalEmail(searchTerm) : undefined}
                                sx={{
                                    flexDirection: currentLang === "ar" ? "row-reverse" : "row",
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    transition: 'background-color 0.2s, opacity 0.2s',
                                    cursor: searchIsValidEmail ? 'pointer' : 'default',
                                    opacity: searchIsValidEmail ? 1 : 0.5,
                                    '&:hover': searchIsValidEmail ? {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    } : {},
                                    padding: '12px 16px',
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main }}>
                                        <MailIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={searchTerm}
                                    secondary={t("shareContractModal.inviteExternal", "Invite as external collaborator")}
                                    sx={{ textAlign: currentLang === "ar" ? "right" : "left" }}
                                    secondaryTypographyProps={{ sx: { color: searchIsValidEmail ? theme.palette.primary.main : theme.palette.text.disabled, fontWeight: 500 } }}
                                />
                                <PersonAddIcon sx={{ color: searchIsValidEmail ? theme.palette.primary.main : theme.palette.text.disabled, ml: 1 }} />
                            </ListItem>
                        )}
                    </List>
                )}

                {/* External invites list */}
                {externalInvites.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                            {t("shareContractModal.externalCollaborators", "External collaborators")}
                        </Typography>
                        {externalInvites.map((invite) => (
                            <Box key={invite.email} sx={{
                                display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1.5,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main, fontSize: 14 }}>
                                    <MailIcon sx={{ fontSize: 16 }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>{invite.email}</Typography>
                                </Box>
                                <Chip
                                    label={t("shareContractModal.externalBadge", "External")}
                                    size="small"
                                    sx={{ fontSize: 11, height: 22, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                                />
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                        value={externalRoles[invite.email] || 'reviewer'}
                                        onChange={(e) => handleExternalRoleChange(invite.email, e.target.value)}
                                        size="small"
                                        sx={{ fontSize: 13 }}
                                    >
                                        {externalAvailableRoles.map((role) => (
                                            <MenuItem key={role} value={role} sx={{ fontSize: 13 }}>
                                                {t(`shareContractModal.role${role.charAt(0).toUpperCase() + role.slice(1)}`, role)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    size="small"
                                    onClick={() => handleRemoveExternal(invite.email)}
                                    sx={{ minWidth: 'auto', color: theme.palette.text.secondary, fontSize: 12 }}
                                >
                                    &times;
                                </Button>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Selected users count */}
                {totalSelected > 0 && (
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <PersonAddIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="body2">
                            {t("shareContractModal.selectedUsersCount", { count: totalSelected }, `${totalSelected} user(s) selected for sharing`)}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onClose}
                    color="inherit"
                    disabled={sharing}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        color: theme.palette.text.secondary
                    }}
                >
                    {t("cancel")}
                </Button>

                <CustomButton
                    text={sharing ? t("shareContractModal.sharing", "Sharing...") : t("share")}
                    disabled={totalSelected === 0 || sharing}
                    outlined={false}
                    loading={sharing}
                    onClick={handleShare}
                    sx={{ ml: 1 }}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ShareContractModal;
